<template>
  <section class="educacional-section">
    <div class="educacional-content">
      <h1 class="page-title">Educacional</h1>

      <i v-if="loading" class="fas fa-spinner loading-big"></i>

      <div v-else-if="platformGroups.length === 0" class="empty-state">
        {{ emptyMessage }}
      </div>

      <template v-else>
        <div class="platform-tabs">
          <button
            v-for="(group, idx) in platformGroups"
            :key="group.platform"
            type="button"
            class="platform-tab"
            :class="{ active: selectedPlatformIndex === idx }"
            @click="selectPlatform(idx)"
          >
            {{ group.label }}
            <span class="tab-count">{{ group.videos.length }}</span>
          </button>
        </div>

        <div v-if="currentVideo" class="player-panel">
          <div class="player-wrap">
            <iframe
              v-if="currentEmbedUrl"
              :key="currentEmbedUrl"
              :src="currentEmbedUrl"
              title="Player educacional"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            />
            <p v-else class="player-error">Não foi possível carregar este vídeo.</p>
          </div>
          <h2 class="player-title">{{ currentVideo.title }}</h2>
        </div>

        <div v-if="selectedGroup" class="video-list">
          <button
            v-for="video in selectedGroup.videos"
            :key="video.id"
            type="button"
            class="video-item"
            :class="{ active: currentVideo && currentVideo.id === video.id }"
            @click="playVideo(video)"
          >
            <span class="video-item-icon"><i class="fas fa-play-circle"></i></span>
            <span class="video-item-title">{{ video.title }}</span>
          </button>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import logger from '../../logger'
import { getActive } from '../api/educationalVideo'
import { get as getSignatures } from '../api/signature'

const PLATFORM_LABELS = {
  profit: 'Profit',
  blackarrow: 'BlackArrow',
  ninjatrader: 'NinjaTrader',
  tradingview: 'TradingView',
  profit_yan: 'Profit Yan',
  blackarrow_yan: 'BlackArrow Yan',
  ninjatrader_yan: 'NinjaTrader Yan'
}

const PLATFORM_ORDER = [
  'profit',
  'blackarrow',
  'ninjatrader',
  'tradingview',
  'profit_yan',
  'blackarrow_yan',
  'ninjatrader_yan'
]

export default {
  name: 'Educacional',
  data () {
    return {
      loading: true,
      videos: [],
      subscribedPlatforms: [],
      selectedPlatformIndex: 0,
      currentVideo: null
    }
  },
  computed: {
    ...mapState(['user']),
    platformGroups () {
      const map = {}
      this.videos.forEach(v => {
        if (!map[v.platform]) {
          map[v.platform] = {
            platform: v.platform,
            label: PLATFORM_LABELS[v.platform] || v.platform,
            videos: []
          }
        }
        map[v.platform].videos.push(v)
      })
      return PLATFORM_ORDER
        .filter(p => map[p])
        .map(p => map[p])
    },
    selectedGroup () {
      return this.platformGroups[this.selectedPlatformIndex] || null
    },
    currentEmbedUrl () {
      if (!this.currentVideo) return null
      return this.youtubeEmbedUrl(this.currentVideo.youtubeUrl)
    },
    emptyMessage () {
      if (!this.subscribedPlatforms.length) {
        return 'Nenhum vídeo disponível. Você precisa de uma assinatura ativa para acessar o conteúdo educacional.'
      }
      return 'Nenhum vídeo disponível no momento para suas assinaturas.'
    }
  },
  async mounted () {
    if (!this.user) {
      this.$router.push('/')
      this.$store.commit('redirect', '/educacional')
      this.$store.commit('showLogin', true)
      return
    }
    document.body.style.backgroundColor = '#000'
    await this.loadVideos()
  },
  methods: {
    youtubeEmbedUrl (url, autoplay = true) {
      const trimmed = String(url || '').trim()
      if (!trimmed) return null
      let id = null
      const watchMatch = trimmed.match(/[?&]v=([^&]+)/i)
      if (watchMatch) id = watchMatch[1]
      else {
        const shortMatch = trimmed.match(/youtu\.be\/([^?&/]+)/i)
        if (shortMatch) id = shortMatch[1]
        else {
          const embedMatch = trimmed.match(/youtube\.com\/embed\/([^?&/]+)/i)
          if (embedMatch) id = embedMatch[1]
        }
      }
      if (!id) return null
      return `https://www.youtube.com/embed/${id}?rel=0${autoplay ? '&autoplay=1' : ''}`
    },
    selectPlatform (idx) {
      this.selectedPlatformIndex = idx
      const group = this.platformGroups[idx]
      if (group && group.videos.length) {
        this.playVideo(group.videos[0])
      }
    },
    playVideo (video) {
      this.currentVideo = video
    },
    parseNextDate (nextDate) {
      if (!nextDate) return null
      const parts = String(nextDate).split('/')
      if (parts.length !== 3) return null
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const year = parseInt(parts[2], 10)
      const date = new Date(year, month, day)
      date.setHours(23, 59, 59, 999)
      return date
    },
    isSignatureExpired (signature) {
      const next = this.parseNextDate(signature.nextDate)
      if (!next) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return next < today
    },
    isSignatureEffectivelyActive (signature) {
      return signature.active && !this.isSignatureExpired(signature)
    },
    getSubscribedPlatforms (signatures) {
      const platforms = new Set()
      ;(signatures || [])
        .filter(s => this.isSignatureEffectivelyActive(s))
        .forEach(s => {
          if (s.platform) platforms.add(s.platform)
        })
      return Array.from(platforms)
    },
    async loadVideos () {
      try {
        this.loading = true
        const [allVideos, signatures] = await Promise.all([
          getActive(),
          getSignatures()
        ])
        this.subscribedPlatforms = this.getSubscribedPlatforms(signatures)
        const allowed = new Set(this.subscribedPlatforms)
        this.videos = (allVideos || []).filter(v => allowed.has(v.platform))
        if (this.platformGroups.length) {
          this.selectPlatform(0)
        }
      } catch (e) {
        logger.error(e, this)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.educacional-section {
  margin-top: $menu-height;
  min-height: calc(100vh - #{$menu-height});
  padding: 24px $padding-mobile 48px;
  color: #fff;
}

.educacional-content {
  max-width: 960px;
  margin: 0 auto;
}

.page-title {
  font-size: 1.75em;
  font-weight: 500;
  margin: 0 0 24px;
  text-align: center;
}

.empty-state {
  text-align: center;
  opacity: 0.8;
  padding: 48px 16px;
}

.loading-big {
  display: block;
  margin: 48px auto;
  font-size: 2em;
  color: $secondary-color;
}

.platform-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  justify-content: center;
}

.platform-tab {
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.9em;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &.active {
    background: linear-gradient(50deg, $primary-color 0%, $secondary-color 100%);
    border-color: transparent;
  }

  .tab-count {
    margin-left: 6px;
    opacity: 0.75;
    font-size: 0.85em;
  }
}

.player-panel {
  margin-bottom: 24px;
}

.player-wrap {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: #111;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.player-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  text-align: center;
  padding: 16px;
}

.player-title {
  font-size: 1.15em;
  font-weight: 500;
  margin: 12px 0 0;
  line-height: 1.4;
}

.video-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.video-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    border-color: $secondary-color;
    background: rgba(141, 110, 244, 0.2);
  }
}

.video-item-icon {
  flex-shrink: 0;
  font-size: 1.4em;
  color: $secondary-color;
}

.video-item-title {
  flex: 1;
  font-size: 0.95em;
  line-height: 1.35;
}

@media screen and (min-width: $desktop-width) {
  .educacional-section {
    margin-left: $menu-width-open;
    padding: 32px $padding-desktop 48px;
  }
}
</style>
